// Composant Donut Chart Réutilisable
import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';

interface DonutChartProps {
  percentage: number;
  label: string;
  title: string;
  color?: string;
  size?: 'small' | 'medium' | 'large';
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const Chart = styled.div<{ percentage: number; color: string; size: string }>`
  position: relative;
  width: ${props => {
    switch (props.size) {
      case 'small': return '100px';
      case 'large': return '160px';
      default: return '140px';
    }
  }};
  height: ${props => {
    switch (props.size) {
      case 'small': return '100px';
      case 'large': return '160px';
      default: return '140px';
    }
  }};
  border-radius: ${theme.borderRadius.full};
  background: conic-gradient(
    ${props => props.color} 0deg,
    ${props => props.color} ${props => props.percentage * 3.6}deg,
    ${theme.colors.surface} ${props => props.percentage * 3.6}deg,
    ${theme.colors.surface} 360deg
  );
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    width: ${props => {
      switch (props.size) {
        case 'small': return '70px';
        case 'large': return '120px';
        default: return '100px';
      }
    }};
    height: ${props => {
      switch (props.size) {
        case 'small': return '70px';
        case 'large': return '120px';
        default: return '100px';
      }
    }};
    border-radius: ${theme.borderRadius.full};
    background: white;
  }

  @media print {
    width: auto;
    height: auto;
    background: none;
    display: inline-block;

    &::before {
      display: none;
    }
  }
`;

const Value = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
`;

const Percentage = styled.div<{ color: string }>`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${props => props.color};
  line-height: 1;
`;

const Label = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-top: ${theme.spacing.xs};
`;

const Title = styled.div`
  text-align: center;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

export const DonutChart: React.FC<DonutChartProps> = React.memo(({
  percentage,
  label,
  title,
  color = theme.colors.primary,
  size = 'medium'
}) => {
  return (
    <Container>
      <Chart percentage={percentage} color={color} size={size}>
        <Value>
          <Percentage color={color}>{percentage}%</Percentage>
          <Label>{label}</Label>
        </Value>
      </Chart>
      <Title>{title}</Title>
    </Container>
  );
});
